<script>
  /**
   * Area App component — renders area hydrographs grouped by river.
   *
   * @module App
   * @prop {string} wfo - Weather Forecast Office code.
   * @prop {Array<Object>} rfc - Array of RFC objects ({ name }).
   * @prop {Object} area - Mapping of gauge id => gauge metadata.
   * @prop {Object} gaugesData - Lookup of gauge id => gauge metadata (upstream/downstream).
   * @prop {Object} gaugeConfig - Gauge configuration JSON.
   *
   * Renders: `Breadcrumb`, `RiverLinks`, `HydroLists`, `Radio`, and `Checkbox`.
   */
  import Breadcrumb from './Breadcrumb.svelte'
  import Checkbox from './Checkbox.svelte'
  import HydroLists from './HydroLists.svelte'
  import Radio from './Radio.svelte'
  import RiverLinks from './RiverLinks.svelte'
  import { ucWords } from './base.js'

  export let wfo
  export let rfc
  export let area
  export let gaugesData
  export let gaugeConfig // gauge configuration as JSON

  let group = 1
  let uniqueRivers = {}
  // Simple regex to split out most river names. TM
  var re = /^.+?\s+(creek|river|stream|lake|reservoir|run|bayou|(at|near))/i

  // Try to combine river names. TM
  Object.keys(area).forEach((key) => {
    var cleanName = ucWords(area[key].name)
    var matchArr = re.exec(cleanName)

    if (matchArr !== null) {
      if (matchArr[2] != undefined) {
        matchArr[0] = matchArr[0].substring(0, matchArr[0].length - matchArr[2].length).trim()
      }

      if (!uniqueRivers[matchArr[0]]) {
        uniqueRivers[matchArr[0]] = []
      }

      uniqueRivers[matchArr[0]].push(area[key])
    } else {
      if (!uniqueRivers[cleanName]) {
        uniqueRivers[cleanName] = []
      }

      uniqueRivers[cleanName].push(area[key])
    }
  })

  /**
 * Returns the upstream gauge ID for a given gauge
 * if it exists in gaugesData and is included in the provided watershed array.
 *
 * @param {string} gauge - The gauge identifier to check.
 * @param {string[]} wbarray - Array of valid gauge IDs (e.g., watershed boundary gauges).
 * @returns {string} The upstream gauge ID if valid and present in wbarray; otherwise an empty string.
 */
  function upstream(gauge, wbarray) {
    if (Object.hasOwn(gaugesData, gauge) && wbarray.includes(gaugesData[gauge].upstreamGauge)) {
      return gaugesData[gauge].upstreamGauge
    } else return ''
  }

  /**
 * Returns the downstream gauge ID for a given gauge
 * if it exists in gaugesData and is included in the provided watershed array.
 *
 * @param {string} gauge - The gauge identifier to check.
 * @param {string[]} wbarray - Array of valid gauge IDs (e.g., watershed boundary gauges).
 * @returns {string} The downstream gauge ID if valid and present in wbarray; otherwise an empty string.
 */
  function downstream(gauge, wbarray) {
    if (Object.hasOwn(gaugesData, gauge) && wbarray.includes(gaugesData[gauge].downstreamGauge)) {
      return gaugesData[gauge].downstreamGauge
    } else return ''
  }

  //Reorder the HGs in up-downstream order
  for (let [key, value] of Object.entries(uniqueRivers)) {
    if (uniqueRivers[key].length > 1) {
      let temp = []
      for (let j = 0; j < uniqueRivers[key].length; j++) {
        if (!temp.includes(uniqueRivers[key][j]['id'])) {
          let up = gaugesData[uniqueRivers[key][j]['id']].upstreamGauge
          let down = gaugesData[uniqueRivers[key][j]['id']].downstreamGauge
          let wbarray = []
          for (let i = 0; i < uniqueRivers[key].length; i++) {
            wbarray.push(uniqueRivers[key][i]['id'])
          }
          let upGauges = []
          let downGauges = []

          //Get all upstream gauges
          while (up != '') {
            upGauges.unshift(up)
            up = upstream(up, wbarray)
          }
          //Get all downstream gauges
          while (down != '') {
            downGauges.push(down)
            down = downstream(down, wbarray)
          }
          //Put all gauges in one array
          temp.push.apply(temp, upGauges.concat([uniqueRivers[key][j]['id']].concat(downGauges)))
          //Remove duplicates that may be added
          temp = [...new Set(temp)]
        }
      }
      //Reset waterbody in correct order with all metadata
      let temp2 = []
      for (let t = 0; t < temp.length; t++) {
        for (let k = 0; k < uniqueRivers[key].length; k++) {
          if (temp[t] == uniqueRivers[key][k]['id']) {
            temp2.push(uniqueRivers[key][k])
          }
        }
      }
      uniqueRivers[key] = temp2
    }
  }
</script>

<Breadcrumb {wfo} {rfc} />

<a name="#top"><h3>Area Hydrographs</h3></a>

<main>
  <div class="list-border uk-flex uk-flex-left uk-grid-small" uk-grid>
    <div class="uk-panel uk-panel-box uk-text-nowrap uk-width-1-6@l uk-width-1-4@m uk-width-1-2@s">
      <Radio bind:group value={1}>Default Hydrographs</Radio>
    </div>
    <div class="uk-panel uk-panel-box uk-text-nowrap uk-width-1-6@l uk-width-1-4@m uk-width-1-2@s">
      <Radio bind:group value={2}>Scale To Flood Categories</Radio>
    </div>
  </div>
  {#if Object.keys(uniqueRivers).length > 0}
    <RiverLinks {uniqueRivers} />
    <HydroLists {group} {gaugeConfig} {uniqueRivers} />
  {:else}
    No rivers found...
  {/if}
</main>

<style>
  main {
    text-align: left;
    padding: 1em;
    max-width: 240px;
    margin: 0 auto;
  }

  h3 {
    color: #ff3e00;
    text-transform: uppercase;
    font-size: 1.5em;
    font-weight: 100;
    margin-left: 18px;
  }

  @media (min-width: 640px) {
    main {
      max-width: none;
    }
  }
  a[name='#top'] {
    text-decoration: none;
  }
</style>
